import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type ApiError, type AuditTrailResult } from '@whinerp/client';
import { Button, ErrorState, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { AuditEntries } from '../shared/audit-entries.js';

/**
 * Setup > Audit log (F-018).
 *
 * Filters on the three questions people actually arrive with: who changed it,
 * what kind of thing changed, and when. "Which agent" is one of them - an
 * enterprise reviewer asks whether AI touched a record before they ask
 * anything else.
 */
export function AuditPanel(): JSX.Element {
  const t = useT();
  const [actorType, setActorType] = useState<'' | 'user' | 'agent' | 'system'>('');
  const [objectKey, setObjectKey] = useState('');

  const trail = useQuery<AuditTrailResult, ApiError>({
    queryKey: ['audit', actorType, objectKey],
    queryFn: () =>
      api.auditTrail({
        query: {
          ...(actorType ? { actorType } : {}),
          ...(objectKey ? { objectKey } : {}),
          limit: 50,
        },
      }),
  });

  return (
    <Panel data-region="audit" id="audit">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('audit.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('audit.body')}</p>

        <AuditExport />

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('audit.filter.actorType')}
            <select
              data-audit-actor-type
              value={actorType}
              onChange={(event) =>
                setActorType(event.currentTarget.value as '' | 'user' | 'agent' | 'system')
              }
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            >
              <option value="">{t('audit.filter.all')}</option>
              <option value="user">{t('audit.actor.user')}</option>
              <option value="agent">{t('audit.actor.agent')}</option>
              <option value="system">{t('audit.actor.system')}</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-dense text-ink-600">
            {t('audit.filter.object')}
            <input
              data-audit-object
              value={objectKey}
              onChange={(event) => setObjectKey(event.currentTarget.value)}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            />
          </label>
        </div>

        {trail.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : trail.isError ? (
          <ErrorState
            title={t('state.error')}
            body={trail.error.message}
            onRetry={() => void trail.refetch()}
          />
        ) : (
          <AuditEntries entries={trail.data.entries} emptyLabel={t('audit.none')} />
        )}
      </div>
    </Panel>
  );
}

/**
 * Takes the whole trail out as hash-chained JSON lines (G-280), page by page
 * so a long trail is not one request, and says the last hash - the number a
 * retention system checks the next pull against.
 */
function AuditExport(): JSX.Element {
  const t = useT();
  const exported = useMutation({
    mutationFn: async () => {
      let jsonl = '';
      let count = 0;
      let afterLogId: string | null = null;
      let previousHash: string | null = null;
      for (let page = 0; page < 50; page += 1) {
        const result = await api.exportAuditLog({ body: { afterLogId, previousHash, limit: 5000 } });
        jsonl += result.jsonl;
        count += result.count;
        afterLogId = result.lastLogId ?? afterLogId;
        previousHash = result.lastHash || previousHash;
        if (!result.more) break;
      }
      return { jsonl, count, lastHash: previousHash ?? '' };
    },
    onSuccess: (file) => {
      try {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([file.jsonl], { type: 'application/x-ndjson' }));
        link.download = 'audit-log.jsonl';
        link.click();
      } catch {
        // No object URLs here (a test, an old browser): the summary still says what was exported.
      }
    },
  });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button intent="secondary" size="small" data-audit-export disabled={exported.isPending} onClick={() => exported.mutate()}>
        {t('audit.export')}
      </Button>
      {exported.data ? (
        <span data-audit-exported className="text-meta text-ink-meta">
          {t('audit.exported', { values: { count: String(exported.data.count), hash: exported.data.lastHash.slice(0, 16) } })}
        </span>
      ) : null}
      {exported.error ? <span className="text-meta text-danger-text">{(exported.error as Error).message}</span> : null}
    </div>
  );
}
